import { useRef } from 'react';
import type { Funcionario } from '@/context/FuncionariosContext';
import { useModalAnimation } from '@/hooks/useModalAnimation';
import { calcularProgressao, temDezoitoAnosEm30Ago2023, PONTOS_REGRA_18_ANOS } from '@/utils/progressaoUtils';
import { useTheme } from '@/context/ThemeContext';
import { useAppSettings } from '@/context/AppSettingsContext';
import { useSiadap } from '@/context/SiadapContext';

interface ExportPdfModalProps {
  elegiveis: Funcionario[];
  pontosPorFuncionario: Record<string, number>;
  limiarPorFuncionario?: Record<string, number>;
  regraEspecialPorFuncionario?: Record<string, boolean>;
  onClose: () => void;
}

export default function ExportPdfModal({
  elegiveis,
  pontosPorFuncionario,
  limiarPorFuncionario = {},
  regraEspecialPorFuncionario = {},
  onClose,
}: ExportPdfModalProps) {
  const { animating, handleClose } = useModalAnimation(onClose);
  const printRef = useRef<HTMLDivElement>(null);
  const { theme } = useTheme();
  const { settings } = useAppSettings();
  const { progressoes } = useSiadap();

  const nomeCamara = theme.nomeCamara || settings.nome_camara || 'Câmara Municipal';
  const cicloSiadap = settings.ciclo_siadap || '2023–2024';
  const pdfTitulo = settings.pdf_titulo || 'Relatório de Elegíveis para Progressão';
  const pdfRodape = settings.pdf_rodape || `SIADAP para RH · ${nomeCamara}`;

  const elegiveisRegra18 = elegiveis.filter((f) => regraEspecialPorFuncionario[f.id]);
  const elegiveisNormal = elegiveis.filter((f) => !regraEspecialPorFuncionario[f.id]);

  const funcionariosComAcelerador = new Set<string>();
  progressoes.forEach((p) => {
    if (p.aceleradorRemuneratorio) funcionariosComAcelerador.add(p.funcionarioId);
  });

  function handlePrint() {
    const printWindow = window.open('', '_blank', 'width=960,height=750');
    if (!printWindow) return;

    const hoje = new Date().toLocaleDateString('pt-PT', { day: '2-digit', month: 'long', year: 'numeric' });

    const renderRows = (lista: Funcionario[], isRegra18: boolean) =>
      lista.map((f) => {
        const pontos = pontosPorFuncionario[f.id] ?? f.pontosAtuais ?? 0;
        const limiar = limiarPorFuncionario[f.id] ?? (isRegra18 ? PONTOS_REGRA_18_ANOS : 8);
        const prog = calcularProgressao(pontos, f.nivelAtual, limiar);
        const dataEntrada = f.dataEntradaFuncaoPublica
          ? new Date(f.dataEntradaFuncaoPublica).toLocaleDateString('pt-PT')
          : '—';
        const temAcelerador = funcionariosComAcelerador.has(f.id);
        return `
          <tr>
            <td style="font-family:monospace;color:#64748b;">#${f.id}</td>
            <td style="font-weight:600;">${f.nome}</td>
            <td>${f.carreira}</td>
            <td style="color:#64748b;">${f.unidadeOrganica || '—'}</td>
            <td style="color:#64748b;">${dataEntrada}</td>
            <td><span class="nivel">${f.nivelAtual}</span><span class="arrow">→</span><span class="nivel-novo">${prog.proximoNivel}</span></td>
            <td><span class="pts">${pontos} pts</span></td>
            <td>${temAcelerador ? '<span class=\"badge badge-acelerador\">Acelerador</span>' : ''}${temAcelerador ? ' ' : ''}<span class="badge ${isRegra18 ? 'badge-regra18' : 'badge-elegivel'}">${isRegra18 ? 'Regra 18 anos' : 'Elegível'}</span></td>
          </tr>
        `;
      }).join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html lang="pt">
      <head>
        <meta charset="UTF-8" />
        <title>${pdfTitulo} — ${hoje}</title>
        <style>
          * { margin:0; padding:0; box-sizing:border-box; }
          body { font-family:'Segoe UI',Arial,sans-serif; font-size:12px; color:#1e293b; background:#fff; padding:32px; }
          .header { border-bottom:2px solid #1e293b; padding-bottom:16px; margin-bottom:24px; }
          .header-top { display:flex; align-items:flex-start; justify-content:space-between; }
          .org-name { font-size:18px; font-weight:700; color:#1e293b; }
          .org-sub { font-size:11px; color:#64748b; margin-top:2px; }
          .report-title { font-size:13px; font-weight:600; color:#1e293b; text-align:right; }
          .report-date { font-size:11px; color:#64748b; text-align:right; margin-top:2px; }
          .summary { display:flex; gap:14px; margin-bottom:24px; }
          .summary-card { flex:1; border:1px solid #e2e8f0; border-radius:8px; padding:12px 16px; }
          .summary-card .value { font-size:22px; font-weight:700; color:#1e293b; }
          .summary-card .label { font-size:10px; color:#64748b; margin-top:2px; text-transform:uppercase; letter-spacing:.05em; }
          .summary-card.amber { border-color:#fde68a; background:#fffbeb; }
          .summary-card.amber .value { color:#92400e; }
          .section-title { font-size:11px; font-weight:700; color:#64748b; text-transform:uppercase; letter-spacing:.08em; margin:20px 0 10px; }
          .section-title.amber { color:#b45309; }
          .notice { background:#fffbeb; border:1px solid #fde68a; border-radius:8px; padding:10px 14px; margin-bottom:14px; font-size:11px; color:#92400e; line-height:1.5; }
          .notice strong { font-weight:700; }
          table { width:100%; border-collapse:collapse; margin-bottom:8px; }
          thead tr { background:#f8fafc; }
          th { padding:8px 10px; text-align:left; font-size:10px; font-weight:700; color:#64748b; text-transform:uppercase; letter-spacing:.05em; border-bottom:1px solid #e2e8f0; }
          td { padding:9px 10px; font-size:11px; border-bottom:1px solid #f1f5f9; vertical-align:middle; }
          tr:last-child td { border-bottom:none; }
          .badge { display:inline-block; padding:2px 8px; border-radius:999px; font-size:10px; font-weight:600; }
          .badge-elegivel { background:#dcfce7; color:#166534; }
          .badge-regra18 { background:#fef3c7; color:#92400e; }
          .badge-acelerador { background:#fffbeb; color:#92400e; border:1px solid #fde68a; }
          .pts { font-weight:700; color:#059669; }
          .nivel { font-weight:600; color:#1e293b; }
          .nivel-novo { font-weight:700; color:#059669; }
          .arrow { color:#94a3b8; margin:0 4px; }
          .divider { border:none; border-top:1px solid #e2e8f0; margin:24px 0; }
          .footer { margin-top:32px; padding-top:12px; border-top:1px solid #e2e8f0; display:flex; justify-content:space-between; font-size:10px; color:#94a3b8; }
          @media print {
            body { padding:20px; }
            @page { margin:1.5cm; size:A4; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="header-top">
            <div>
              <div class="org-name">${nomeCamara}</div>
              <div class="org-sub">SIADAP para RH</div>
            </div>
            <div>
              <div class="report-title">${pdfTitulo}</div>
              <div class="report-date">Emitido em ${hoje}</div>
            </div>
          </div>
        </div>

        <div class="summary">
          <div class="summary-card">
            <div class="value">${elegiveis.length}</div>
            <div class="label">Total elegíveis</div>
          </div>
          <div class="summary-card">
            <div class="value">${elegiveisNormal.length}</div>
            <div class="label">Regra geral</div>
          </div>
          <div class="summary-card amber">
            <div class="value">${elegiveisRegra18.length}</div>
            <div class="label">Regra 18 anos (30/08/2023)</div>
          </div>
          <div class="summary-card">
            <div class="value">${elegiveis.length > 0 ? (elegiveis.reduce((s, f) => s + (pontosPorFuncionario[f.id] ?? f.pontosAtuais ?? 0), 0) / elegiveis.length).toFixed(1) : '—'}</div>
            <div class="label">Média de pontos</div>
          </div>
          <div class="summary-card">
            <div class="value">${new Date().getFullYear()}</div>
            <div class="label">Ciclo avaliativo</div>
          </div>
        </div>

        ${elegiveisRegra18.length > 0 ? `
          <div class="section-title amber">Funcionários elegíveis pela regra especial — 18 anos de serviço a 30/08/2023</div>
          <div class="notice">
            <strong>Regra especial 2024 (art.º aplicável):</strong> Funcionários com 18 ou mais anos de serviço na função pública
            <strong>a 30 de agosto de 2023</strong> têm direito a progressão com apenas <strong>${PONTOS_REGRA_18_ANOS} pontos</strong>
            (em vez do limiar geral). Esta condição é verificada exclusivamente à data de referência legal de 30/08/2023.
          </div>
          <table>
            <thead>
              <tr>
                <th>Nº</th><th>Nome</th><th>Carreira</th><th>Unidade Orgânica</th>
                <th>Entrada FP</th><th>Nível</th><th>Pontos</th><th>Estado</th>
              </tr>
            </thead>
            <tbody>${renderRows(elegiveisRegra18, true)}</tbody>
          </table>
          <hr class="divider" />
        ` : ''}

        <div class="section-title">Funcionários elegíveis pela regra geral</div>
        ${elegiveisNormal.length === 0 ? '<p style="font-size:11px;color:#94a3b8;margin-bottom:16px;">Nenhum funcionário elegível pela regra geral.</p>' : `
          <table>
            <thead>
              <tr>
                <th>Nº</th><th>Nome</th><th>Carreira</th><th>Unidade Orgânica</th>
                <th>Entrada FP</th><th>Nível</th><th>Pontos</th><th>Estado</th>
              </tr>
            </thead>
            <tbody>${renderRows(elegiveisNormal, false)}</tbody>
          </table>
        `}

        <div class="footer">
          <span>${pdfRodape}</span>
          <span>Ciclo avaliativo ${cicloSiadap} · Documento gerado automaticamente</span>
        </div>
      </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 500);
  }

  const hoje = new Date().toLocaleDateString('pt-PT', { day: '2-digit', month: 'long', year: 'numeric' });

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={handleClose}>
      <div className={`absolute inset-0 bg-black/50 backdrop-blur-sm ${animating ? 'modal-backdrop-active' : 'modal-backdrop-enter'}`} />
      <div
        className={`relative bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden ${animating ? 'modal-panel-active' : 'modal-panel-enter'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-slate-100 rounded-xl text-slate-600 flex-shrink-0">
              <i className="ri-file-pdf-line text-xl" />
            </div>
            <div>
              <h2 className="font-bold text-slate-800 text-base">Exportar Relatório PDF</h2>
              <p className="text-xs text-slate-400 mt-0.5">{nomeCamara} · Funcionários elegíveis para progressão · {hoje}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Preview */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {/* Cards de resumo */}
          <div className="grid grid-cols-5 gap-3 mb-5">
            {[
              { value: elegiveis.length, label: 'Total elegíveis', icon: 'ri-user-star-line', color: 'text-emerald-600 bg-emerald-50' },
              { value: elegiveisNormal.length, label: 'Regra geral', icon: 'ri-checkbox-circle-line', color: 'text-emerald-600 bg-emerald-50' },
              { value: elegiveisRegra18.length, label: 'Regra 18 anos', icon: 'ri-award-fill', color: 'text-amber-600 bg-amber-50' },
              { value: elegiveis.length > 0 ? (elegiveis.reduce((s, f) => s + (pontosPorFuncionario[f.id] ?? f.pontosAtuais ?? 0), 0) / elegiveis.length).toFixed(1) : '—', label: 'Média pts', icon: 'ri-bar-chart-line', color: 'text-slate-600 bg-slate-100' },
              { value: new Date().getFullYear(), label: 'Ciclo', icon: 'ri-calendar-line', color: 'text-slate-600 bg-slate-100' },
            ].map((s) => (
              <div key={s.label} className="bg-slate-50 rounded-xl p-3 flex items-center gap-2.5">
                <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${s.color}`}>
                  <i className={`${s.icon} text-sm`} />
                </div>
                <div>
                  <p className="text-base font-bold text-slate-800 leading-tight">{s.value}</p>
                  <p className="text-xs text-slate-500 leading-tight">{s.label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Secção regra especial */}
          {elegiveisRegra18.length > 0 && (
            <div className="mb-5">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-5 h-5 flex items-center justify-center text-amber-600">
                  <i className="ri-award-fill text-sm" />
                </div>
                <p className="text-xs font-bold text-amber-700 uppercase tracking-widest">
                  Regra especial — 18 anos a 30/08/2023 ({elegiveisRegra18.length})
                </p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3">
                <p className="text-xs text-amber-700 leading-relaxed">
                  Funcionários com <strong>18+ anos de serviço a 30/08/2023</strong> (data de referência legal).
                  Limiar reduzido para <strong>{PONTOS_REGRA_18_ANOS} pts</strong> em vez do limiar geral.
                </p>
              </div>
              <div className="bg-white border border-amber-100 rounded-xl overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-amber-50 border-b border-amber-100">
                      {['Nº', 'Nome', 'Carreira', 'Entrada FP', 'Nível', 'Pontos', 'Acelerador'].map((h) => (
                        <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-amber-700 uppercase tracking-wide">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {elegiveisRegra18.map((f, i) => {
                      const pontos = pontosPorFuncionario[f.id] ?? f.pontosAtuais ?? 0;
                      const limiar = limiarPorFuncionario[f.id] ?? PONTOS_REGRA_18_ANOS;
                      const prog = calcularProgressao(pontos, f.nivelAtual, limiar);
                      const avatarColors = ['bg-amber-100 text-amber-700', 'bg-orange-100 text-orange-700', 'bg-rose-100 text-rose-700'];
                      const colorClass = avatarColors[i % avatarColors.length];
                      const initials = f.nome.split(' ').filter((_, idx, arr) => idx === 0 || idx === arr.length - 1).map((n) => n[0]).join('');
                      const dataEntrada = f.dataEntradaFuncaoPublica
                        ? new Date(f.dataEntradaFuncaoPublica).toLocaleDateString('pt-PT')
                        : '—';
                      const anosEm30ago = f.dataEntradaFuncaoPublica
                        ? Math.floor((new Date(2023, 7, 30).getTime() - new Date(f.dataEntradaFuncaoPublica).getTime()) / (365.25 * 86400000))
                        : null;
                      return (
                        <tr key={f.id} className="border-b border-amber-50 last:border-0 hover:bg-amber-50/40">
                          <td className="px-4 py-3 text-xs text-slate-400 font-mono">#{f.id}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-bold flex-shrink-0 ${colorClass}`}>
                                {initials}
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-slate-800">{f.nome}</p>
                                {anosEm30ago !== null && (
                                  <p className="text-xs text-amber-600">{anosEm30ago} anos a 30/08/2023</p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-600">{f.carreira}</td>
                          <td className="px-4 py-3 text-xs text-slate-500">{dataEntrada}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <span className="text-sm font-bold text-slate-700">{f.nivelAtual}</span>
                              <i className="ri-arrow-right-line text-xs text-slate-300" />
                              <span className="text-sm font-bold text-emerald-600">{prog.proximoNivel}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5">
                              <span className="text-sm font-bold text-amber-600">{pontos} pts</span>
                              <span className="text-xs text-amber-500">/ {limiar}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {funcionariosComAcelerador.has(f.id) ? (
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                                <i className="ri-flashlight-line text-[9px]" />
                                Sim
                              </span>
                            ) : (
                              <span className="text-xs text-slate-300">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Secção regra geral */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-5 h-5 flex items-center justify-center text-emerald-600">
                <i className="ri-checkbox-circle-line text-sm" />
              </div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                Regra geral ({elegiveisNormal.length})
              </p>
            </div>
            {elegiveisNormal.length === 0 ? (
              <div className="bg-slate-50 rounded-xl py-8 flex flex-col items-center gap-2 text-slate-400">
                <div className="w-8 h-8 flex items-center justify-center"><i className="ri-inbox-line text-2xl" /></div>
                <p className="text-sm">Nenhum funcionário elegível pela regra geral.</p>
              </div>
            ) : (
              <div className="bg-white border border-slate-100 rounded-xl overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                      {['Nº', 'Nome', 'Carreira', 'Entrada FP', 'Nível', 'Pontos', 'Acelerador'].map((h) => (
                        <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {elegiveisNormal.map((f, i) => {
                      const pontos = pontosPorFuncionario[f.id] ?? f.pontosAtuais ?? 0;
                      const limiar = limiarPorFuncionario[f.id] ?? 8;
                      const prog = calcularProgressao(pontos, f.nivelAtual, limiar);
                      const avatarColors = ['bg-rose-100 text-rose-700', 'bg-teal-100 text-teal-700', 'bg-amber-100 text-amber-700', 'bg-sky-100 text-sky-700', 'bg-orange-100 text-orange-700'];
                      const colorClass = avatarColors[i % avatarColors.length];
                      const initials = f.nome.split(' ').filter((_, idx, arr) => idx === 0 || idx === arr.length - 1).map((n) => n[0]).join('');
                      const dataEntrada = f.dataEntradaFuncaoPublica
                        ? new Date(f.dataEntradaFuncaoPublica).toLocaleDateString('pt-PT')
                        : '—';
                      return (
                        <tr key={f.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50">
                          <td className="px-4 py-3 text-xs text-slate-400 font-mono">#{f.id}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-bold flex-shrink-0 ${colorClass}`}>
                                {initials}
                              </div>
                              <span className="text-sm font-semibold text-slate-800">{f.nome}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-600">{f.carreira}</td>
                          <td className="px-4 py-3 text-xs text-slate-500">{dataEntrada}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <span className="text-sm font-bold text-slate-700">{f.nivelAtual}</span>
                              <i className="ri-arrow-right-line text-xs text-slate-300" />
                              <span className="text-sm font-bold text-emerald-600">{prog.proximoNivel}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5">
                              <span className="text-sm font-bold text-emerald-600">{pontos} pts</span>
                              <span className="text-xs text-slate-400">/ {limiar}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {funcionariosComAcelerador.has(f.id) ? (
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                                <i className="ri-flashlight-line text-[9px]" />
                                Sim
                              </span>
                            ) : (
                              <span className="text-xs text-slate-300">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between flex-shrink-0">
          <p className="text-xs text-slate-400">
            O relatório será aberto numa nova janela para impressão/exportação PDF.
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={handleClose}
              className="whitespace-nowrap px-4 py-2 text-sm text-slate-600 hover:text-slate-800 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
            >
              Fechar
            </button>
            <button
              onClick={handlePrint}
              disabled={elegiveis.length === 0}
              className="whitespace-nowrap px-5 py-2 text-sm bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors cursor-pointer flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-printer-line text-sm" /></div>
              Imprimir / Exportar PDF
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
